import { createContext, useContext, useState } from "react";
import {
	Permission,
	sets as permissions_sets,
} from "@components/Auth/permissions";
import { useCallAPI } from "@hooks/useApi";
import toasty from "@/app/util/toasty";
import { insertOrDeletePermission } from "./utils";
import { useAuth } from "@/app/hooks/useAuth";

const UserPermissionsContext = createContext(null);

export function UserPermissionsProvider({
	children,
	user,
	initialPermissionSet = "",
	onMutate,
}) {
	const callAPI = useCallAPI();
	const { user: authUser, checkPermissions } = useAuth();
	const [selectedPermissionSet, setSelectedPermissionSet] =
		useState(initialPermissionSet);
	const [globalPermissions, setGlobalPermissions] = useState([]);
	const [organisationPermissions, setOrganisationPermissions] = useState({});

	const saveAllPermissions = async (organisationId = null, create = false) => {
		try {
			const userId = user.id;
			const operations = [];
			let created = false;

			const isSealord = checkPermissions(
				"none",
				Permission.SEALORD,
				false
			);
			const canEditGlobalPermissions = isSealord || !!authUser?.uwr;
			if (canEditGlobalPermissions) {
				const user_global_permissions = await callAPI("permissions", {
					query: {
						user_id: `eq.${userId}`,
						organisation_id: `is.null`,
					},
				});
				const editableGlobalPermissions = globalPermissions.filter(
					(permission) =>
						(permission.sealord && isSealord) ||
						(permission.uwr && authUser?.uwr)
				);

				operations.push(
					...insertOrDeletePermission(
						userId,
						organisationId,
						editableGlobalPermissions,
						user_global_permissions,
						callAPI
					)
				);
			}

			// Save organisation permissions if organisationId is provided
			if (organisationId) {
				const orgPerms = organisationPermissions[organisationId];
				if (!orgPerms) {
					throw new Error("Organisation permissions not found");
				}

				// Save permission set tag if provided
				if (orgPerms.permissionSet) {
					await callAPI("rpc/users_organisations_upsert", {
						method: "POST",
						payload: {
							uid: userId,
							oid: organisationId,
							tg: [orgPerms.permissionSet],
						},
						one: true,
						schema: "portal",
					});
					created = true;
				}

				const user_permissions = await callAPI("permissions", {
					query: {
						user_id: `eq.${userId}`,
						organisation_id: `eq.${organisationId}`,
					},
				});

				const allItems = orgPerms.permissions.flatMap((p) => p.items);

				operations.push(
					...insertOrDeletePermission(
						userId,
						organisationId,
						allItems,
						user_permissions,
						callAPI
					)
				);
			}
			if (operations.length > 0 || (create && created)) {
				await Promise.all(operations);
				toasty.success("Permissions updated");
				if (onMutate) onMutate();
				return true;
			} else {
				toasty.warn("No changes made");
				return false;
			}
		} catch (error) {
			toasty.error("Failed to update permissions", error);
			return false;
		}
	};

	const value = {
		selectedPermissionSet,
		setSelectedPermissionSet,
		permissionSets: permissions_sets,
		user,
		globalPermissions,
		setGlobalPermissions,
		organisationPermissions,
		setOrganisationPermissions,
		saveAllPermissions,
	};

	return (
		<UserPermissionsContext.Provider value={value}>
			{children}
		</UserPermissionsContext.Provider>
	);
}

export function useUserPermissions() {
	const context = useContext(UserPermissionsContext);
	if (!context) {
		throw new Error(
			"useUserPermissions must be used within a UserPermissionsProvider"
		);
	}
	return context;
}
